import { CALENDAR } from '../sim/tracks/index';
import { TEAMS } from '../sim/teams';

/**
 * Online play wire protocol (design.md B8.2): JSON over one WebSocket at
 * /ws. The server only relays inputs and keeps time; every device runs the
 * same deterministic sim. Shared by the browser and the server.
 */

export const PROTOCOL = 1;
export const MAX_PLAYERS = 4;
/** Your own input is applied this many ticks late (25 ms). */
export const INPUT_DELAY = 3;
/** Never run more than this far ahead of everyone's confirmed input (400 ms). */
export const MAX_PREDICT = 48;
/** A player this many ticks behind the leader's input is dropped (3 s). */
export const LAG_DROP_TICKS = 360;
export const HASH_EVERY = 240;

export type OnlineMode = 'race' | 'h2h';

export interface OnlineSettings {
  mode: OnlineMode;
  track: string;
  /** Everyone drives this car's performance (liveries differ). */
  car: string;
  laps: number;
  lights: number;
  cpus: number;
}

export const DEFAULT_SETTINGS: OnlineSettings = { mode: 'race', track: CALENDAR[0].id, car: 'lotos', laps: 3, lights: 5, cpus: 0 };

export const LAP_CHOICES = [2, 3, 5];
export const LIGHT_CHOICES = [3, 5, 8];

export function cleanSettings(s: Partial<OnlineSettings>, prev: OnlineSettings = DEFAULT_SETTINGS): OnlineSettings {
  const mode = s.mode === 'h2h' || s.mode === 'race' ? s.mode : prev.mode;
  const track = CALENDAR.some((d) => d.id === s.track) ? (s.track as string) : prev.track;
  const car = TEAMS.some((t) => t.id === s.car) ? (s.car as string) : prev.car;
  const laps = LAP_CHOICES.includes(s.laps as number) ? (s.laps as number) : prev.laps;
  const lights = LIGHT_CHOICES.includes(s.lights as number) ? (s.lights as number) : prev.lights;
  const maxCpu = mode === 'h2h' ? 3 : 5;
  const cpus = Number.isInteger(s.cpus) ? Math.max(0, Math.min(maxCpu, s.cpus as number)) : Math.min(prev.cpus, maxCpu);
  return { mode, track, car, laps, lights, cpus };
}

export interface RoomPlayer {
  id: number;
  name: string;
  color: number;
  connected: boolean;
  /** Racing in the current race (false = waiting for the next one). */
  racing: boolean;
}

export interface RoomInfo {
  code: string;
  you: number;
  host: number;
  players: RoomPlayer[];
  settings: OnlineSettings;
  state: 'lobby' | 'racing';
}

/** Who drives which car in a race: human racers come first, then CPUs. */
export interface Roster {
  player: number;
  slot: number;
  name: string;
}

export type ClientMsg =
  | { t: 'create'; name: string; color: number; v: number }
  | { t: 'join'; code: string; name: string; color: number; v: number }
  | { t: 'set'; settings: Partial<OnlineSettings> }
  | { t: 'start' }
  | { t: 'lobby' }
  | { t: 'in'; f: number; i: [number, number, number][] }
  | { t: 'hash'; f: number; h: number }
  | { t: 'done' }
  | { t: 'ping'; c: number }
  | { t: 'leave' };

export type ServerMsg =
  | { t: 'room'; room: RoomInfo }
  | { t: 'start'; seed: number; startAt: number; roster: Roster[]; settings: OnlineSettings }
  | { t: 'in'; s: number; f: number; i: [number, number, number][] }
  | { t: 'gone'; s: number; f: number }
  | { t: 'pong'; c: number; s: number }
  | { t: 'desync'; f: number }
  | { t: 'error'; msg: string; code?: string };

/** Room codes: no vowels (no rude words), no 0/O/1/I. */
export const CODE_ALPHABET = 'BCDFGHJKLMNPQRSTVWXZ23456789';
export const CODE_LENGTH = 5;

export function isRoomCode(s: string): boolean {
  return s.length === CODE_LENGTH && [...s].every((c) => CODE_ALPHABET.includes(c));
}
